import { company, isString, json, record } from '../data/members'
import { request, type WriteOutcome } from '../hypotheses/dictionaryApi'
import type { VerdictMetric } from '../creatives/contract'

/** `GET /api/settings`: the stored fields, the derived reporting currency and the role's right to edit. */
export interface CompanySettings {
  primaryMetric: VerdictMetric
  /** A ROAS ratio or a CPA in the reporting currency, by `primaryMetric`; `null` is no target. */
  target: number | null
  /** The stored currency, or the derived one while nothing is stored; `null` without accounts. */
  reportingCurrency: string | null
  reportingCurrencySet: boolean
  /** The stored currency is no longer among `currencies`. */
  reportingCurrencyStale: boolean
  /** The currency the 90-day spend derives, whatever is stored; `null` without accounts. */
  derivedReportingCurrency: string | null
  /** The distinct currencies of the company's accounts. */
  currencies: string[]
  tiers: { direction: number; provisional: number; reliable: number }
  stopLossMultiple: number
  waitingDays: number
  updatedAt: string | null
  canEdit: boolean
}

/** The six fields `PUT /api/settings` edits; the screen always sends all of them. */
export interface SettingsEdit {
  primaryMetric: VerdictMetric
  target: number | null
  reportingCurrency: string | null
  tiers: { direction: number; provisional: number; reliable: number }
  stopLossMultiple: number
  waitingDays: number
}

/** The field-level `422` codes; each names the field it refuses. */
export type SettingsField = 'metric' | 'target' | 'currency' | 'tiers' | 'multiple' | 'waiting'

export const SETTINGS_ERROR_FIELDS: Record<string, SettingsField> = {
  invalid_primary_metric: 'metric',
  invalid_target: 'target',
  invalid_reporting_currency: 'currency',
  cpa_target_needs_currency: 'target',
  invalid_tiers: 'tiers',
  invalid_stop_loss_multiple: 'multiple',
  invalid_waiting_days: 'waiting',
}

export function settingsUrl(companyId: string): string {
  return `/api/settings?${company(companyId)}`
}

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const nullable = <T,>(value: unknown, is: (v: unknown) => v is T): value is T | null => value === null || is(value)

/** The settings body, or `null` when it is not one. */
export function parseSettings(body: unknown): CompanySettings | null {
  const b = record(body)
  if (b === null) return null
  const { primaryMetric, target, reportingCurrency, currencies, canEdit } = b
  if (primaryMetric !== 'roas' && primaryMetric !== 'cpa') return null
  if (!nullable(target, isNumber) || !nullable(reportingCurrency, isString)) return null
  if (!Array.isArray(currencies) || !currencies.every(isString)) return null
  if (typeof canEdit !== 'boolean') return null
  const tiers = record(b.tiers)
  const derived = b.derivedReportingCurrency === undefined ? reportingCurrency : b.derivedReportingCurrency
  if (!nullable(derived, isString)) return null
  return {
    primaryMetric,
    target,
    reportingCurrency,
    reportingCurrencySet: b.reportingCurrencySet === true,
    reportingCurrencyStale: b.reportingCurrencyStale === true,
    derivedReportingCurrency: derived,
    currencies,
    tiers: {
      direction: isNumber(tiers?.direction) ? tiers.direction : 20,
      provisional: isNumber(tiers?.provisional) ? tiers.provisional : 50,
      reliable: isNumber(tiers?.reliable) ? tiers.reliable : 100,
    },
    stopLossMultiple: isNumber(b.stopLossMultiple) ? b.stopLossMultiple : 5,
    waitingDays: isNumber(b.waitingDays) ? b.waitingDays : 30,
    updatedAt: isString(b.updatedAt) ? b.updatedAt : null,
    canEdit,
  }
}

export function readSettings(companyId: string, fetchImpl?: typeof fetch): Promise<WriteOutcome<CompanySettings>> {
  return request(settingsUrl(companyId), {}, parseSettings, fetchImpl)
}

export function saveSettings(
  companyId: string,
  edit: SettingsEdit,
  fetchImpl?: typeof fetch,
): Promise<WriteOutcome<CompanySettings>> {
  return request(settingsUrl(companyId), json('PUT', edit), parseSettings, fetchImpl)
}
